import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { AppTabList, type TabType } from "@components/AppTabList.tsx";
import { TabContext, TabPanel } from "@mui/lab";
import { Tab, Stack } from "@mui/material";
import { useCurrentParticipation } from "@/contexts/ParticipationContext";
import { ROLE_ADMIN, ROLE_USER } from "@/constants";
import ParticipationDetailsPage from "@components/modules/participations/ParticipationDetailsPage.tsx";
import UserQuestionnaireTab from "@components/modules/questionnaire/UserQuestionnaireTab.tsx";
import ParticipationDocumentsTab from "@components/participations/documents/ParticipationDocumentsTab.tsx";
import AppCard from "@components/AppCard.tsx";

import { Icon } from "@iconify/react";
import { requireRole } from "@/auth/requireRole.ts";
import type { CommonSearchParams } from "@/types/router/types.ts";

export const Route = createFileRoute("/_dashboard/participations/$uuid/$tab")({
  beforeLoad: (args) => {
    const { context } = args;
    requireRole([ROLE_ADMIN, ROLE_USER])(args);
    return { session: context.session! };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { tab } = Route.useParams();
  const searchParams = useSearch({ strict: false }) as CommonSearchParams;
  const data = useCurrentParticipation();
  const { session } = Route.useRouteContext();

  const isModuleOpen = data.module?.status.id === "OPEN";

  const allowedParticipationStatuses = ["REGISTERED", "IN_PROGRESS"];
  const isParticipationAllowed = allowedParticipationStatuses.includes(
    data.status?.id
  );

  const canAccessFormulaire = isModuleOpen && isParticipationAllowed;

  const questionnaireSessions = data.questionnaireSessions;
  const hasQuestionnaireSession =
    questionnaireSessions && questionnaireSessions?.length > 0;
  const isAuthorizedUser =
    session?.user?.uuid === data.laboratoryUser?.uuid ||
    data.authorizedUsers?.some((user) => user.uuid === session?.user?.uuid);

  const getFormulaireLabel = () => {
    if (!isAuthorizedUser) {
      return "Formulaire du participant";
    }
    return hasQuestionnaireSession
      ? "Saisir vos réponses"
      : "Consulter vos réponses";
  };

  const tabs: TabType[] = [
    {
      value: "overview",
      label: "Informations",
      href: `/participations/${data.uuid}/overview`,
      roles: [ROLE_USER, ROLE_ADMIN],
      icon: "tabler:file-text",
      dataPw: "overview_tab",
      children: (
        <ParticipationDetailsPage
          isAdmin={session.user!.isAdmin}
          participation={data}
        />
      ),
    },
    ...(canAccessFormulaire
      ? [
          {
            value: "formulaire",
            label: getFormulaireLabel(),
            href: `/participations/${data.uuid}/formulaire`,
            roles: [ROLE_USER, ROLE_ADMIN],
            icon: "tabler:box",
            dataPw: "formulaire_tab",
            children: <UserQuestionnaireTab />,
          },
        ]
      : []),
    {
      value: "documents",
      label: "Documents",
      href: `/participations/${data.uuid}/documents`,
      roles: [ROLE_USER, ROLE_ADMIN],
      icon: "tabler:files",
      dataPw: "documents_tab",
      children: (
        <AppCard>
          <ParticipationDocumentsTab />
        </AppCard>
      ),
    },
  ];

  const buildHref = (baseHref: string) => {
    if (searchParams.isLaboratoryParticipation) {
      return `${baseHref}?isLaboratoryParticipation=${searchParams.isLaboratoryParticipation}`;
    }
    return baseHref;
  };

  return (
    <Stack gap={2}>
      <TabContext value={tab}>
        <AppTabList>
          {tabs.map((tab) => (
            <Tab
              component={Link}
              key={tab.value}
              label={tab.label}
              value={tab.value}
              href={buildHref(tab.href ?? tab.value)}
              {...(tab.icon && {
                icon: <Icon fontSize="18" icon={tab.icon} />,
              })}
              iconPosition={"start"}
              data-pw={tab.dataPw}
            />
          ))}
        </AppTabList>
        {tabs.map((tab) => (
          <TabPanel
            value={tab.value}
            key={tab.value}
            sx={{
              padding: 0,
            }}
          >
            {tab.children}
          </TabPanel>
        ))}
      </TabContext>
    </Stack>
  );
}
